import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  Snackbar
} from '@mui/material'
import FacebookIcon from '@mui/icons-material/Facebook'
import Text from '../Text'
import TextFieldCustom from '../TextFieldCustom'
import hipersendService from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import { loadFacebookSdk } from '../../libs/facebookSdk'
import { parseEmbeddedSignupMessage } from '../../libs/embeddedSignupMessage'

// Sem as duas envs configuradas, o Embedded Signup simplesmente não existe pro
// cliente — o formulário manual (Number ID/Business ID/Token) continua sendo
// o único caminho, exatamente como já funcionava antes desta feature.
export function isEmbeddedSignupAvailable() {
  return !!(process.env.REACT_APP_FB_APP_ID && process.env.REACT_APP_FB_CONFIG_ID)
}

// Códigos com tratamento dedicado (PIN, múltiplos números, número ausente) são
// resolvidos antes de chegar aqui. Os demais (META_CODE_INVALID,
// META_TOKEN_INVALID, META_SUBSCRIBE_FAILED, META_REGISTER_FAILED, etc.) caem
// direto na mensagem que o master já manda pronta em `message`.
const FALLBACK_ERROR_MESSAGE = 'Não foi possível conectar com o Facebook. Tente novamente.'

// Quanto tempo esperar pelo postMessage de FINISH depois que o FB.login já
// devolveu o code. Vencido o prazo, NÃO desiste: tenta conectar só com o code
// (o master descobre a WABA pelos escopos granulares do token) — o postMessage
// se perde fácil no navegador do cliente (extensão, variação de evento do ES).
const NO_FINISH_TIMEOUT_MS = 8000

/**
 * Botão "Entrar com Facebook" do Embedded Signup da Meta (ES v4).
 *
 * Fluxo: FB.login abre o popup oficial da Meta → o popup manda o waba_id/
 * phone_number_id via window.postMessage (evento WA_EMBEDDED_SIGNUP/FINISH) →
 * o FB.login callback devolve o `code` de uso único → assim que os dois
 * chegarem (em qualquer ordem), trocamos o code pelo token direto no master.
 *
 * O listener de mensagem é registrado no mount, bem antes de qualquer clique
 * — só o FB.login em si precisa ser síncrono dentro do handler do clique
 * (senão o navegador bloqueia o popup).
 */
function MetaEmbeddedSignupButton({ onConnected }) {
  const { getCredentials } = useContext(ApiDataWrapperContext)
  const [sdkStatus, setSdkStatus] = useState('loading') // loading | ready | error
  const [flowStatus, setFlowStatus] = useState('idle') // idle | connecting | pin-required | choose-phone | error
  const [errorMessage, setErrorMessage] = useState('')
  const [pin, setPin] = useState('')
  const [phoneOptions, setPhoneOptions] = useState([])
  const [selectedPhoneId, setSelectedPhoneId] = useState('')
  const [cancelNotice, setCancelNotice] = useState('')

  // instância do FB retornada pelo loader — evita depender de window.FB
  // espalhado pelo componente (mais fácil de testar e de raciocinar)
  const fbRef = useRef(null)
  // dados coletados durante o fluxo do popup: code (do FB.login) + waba/phone
  // (do postMessage). Não precisam causar re-render sozinhos.
  const signupRef = useRef({ code: null, wabaId: null, phoneNumberId: null })
  // sempre aponta pra versão mais atual do handler — o listener em si só é
  // registrado UMA vez, então nunca pode ficar preso numa closure velha
  const handleMessageRef = useRef(() => {})
  // timer do "code chegou, mas o postMessage de FINISH nunca chegou" — só
  // mexe em refs/setState, então não precisa ser recriado a cada render
  const noFinishTimeoutRef = useRef(null)
  // versão sempre-fresca do runExchange pro timer acima (mesmo padrão do
  // handleMessageRef: o setTimeout não pode ficar preso numa closure velha)
  const runExchangeRef = useRef(() => {})

  const clearNoFinishTimeout = () => {
    if (noFinishTimeoutRef.current) {
      clearTimeout(noFinishTimeoutRef.current)
      noFinishTimeoutRef.current = null
    }
  }

  const armNoFinishTimeout = () => {
    clearNoFinishTimeout()
    noFinishTimeoutRef.current = setTimeout(() => {
      noFinishTimeoutRef.current = null
      // guard: só age se o par code+waba continuar incompleto. Em qualquer
      // conexão bem-sucedida (ou já em pin-required/choose-phone) o wabaId já
      // foi preenchido — o timer vira no-op sozinho.
      //
      // Com o code em mãos, dá pra conectar MESMO sem os dados do popup: o
      // master descobre a WABA pelos escopos granulares do próprio token
      // (debug_token). O postMessage se perde fácil no navegador do cliente
      // (extensão, variação de evento do fluxo) — então tenta, em vez de
      // desistir com erro.
      if (signupRef.current.code && !signupRef.current.wabaId) {
        runExchangeRef.current()
      }
    }, NO_FINISH_TIMEOUT_MS)
  }

  const runExchange = useCallback(async (extra = {}) => {
    clearNoFinishTimeout()

    if (extra.phoneNumberId) {
      signupRef.current.phoneNumberId = extra.phoneNumberId
    }

    const payload = {
      code: signupRef.current.code,
      wabaId: signupRef.current.wabaId,
      phoneNumberId: signupRef.current.phoneNumberId,
      ...(extra.pin ? { pin: extra.pin } : {})
    }

    // só o code é obrigatório: sem wabaId o master resolve a WABA pelo token
    if (!payload.code) return

    const { api, secret } = getCredentials()

    setFlowStatus('connecting')
    setErrorMessage('')

    try {
      const response = await hipersendService.notificacoesInteligentes.embeddedSignup(api, secret, payload)

      if (response?.connected) {
        signupRef.current = { code: null, wabaId: null, phoneNumberId: null }
        setFlowStatus('idle')
        if (typeof onConnected === 'function') {
          await onConnected()
        }
        return
      }

      setFlowStatus('error')
      setErrorMessage(FALLBACK_ERROR_MESSAGE)
    } catch (error) {
      const data = error?.response?.data

      if (data?.code === 'META_PIN_REQUIRED') {
        setFlowStatus('pin-required')
        return
      }

      if (data?.code === 'META_MULTIPLE_PHONE_NUMBERS') {
        setPhoneOptions(Array.isArray(data.phoneNumbers) ? data.phoneNumbers : [])
        setFlowStatus('choose-phone')
        return
      }

      if (data?.code === 'META_NO_PHONE_NUMBER') {
        setFlowStatus('error')
        setErrorMessage('Cadastre/verifique um número na sua conta do WhatsApp Business e tente novamente.')
        return
      }

      console.error(error)
      setFlowStatus('error')
      setErrorMessage(data?.message || FALLBACK_ERROR_MESSAGE)
    }
  }, [getCredentials, onConnected])
  runExchangeRef.current = runExchange

  useEffect(() => {
    handleMessageRef.current = (event) => {
      const payload = parseEmbeddedSignupMessage(event)
      if (!payload) return

      // rastro de campo: sem isso é impossível diagnosticar remotamente qual
      // evento a Meta mandou (não carrega nada sensível — waba/número não são
      // segredo)
      console.info('[embedded-signup] evento da Meta:', payload.event, payload.data)

      // o ES v4 não emite só "FINISH": há variantes como FINISH_ONLY_WABA
      // (número não concluiu a verificação dentro do fluxo — vem só o waba_id;
      // o master resolve o número via /phone_numbers) e
      // FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING (coexistência com o app).
      // Tratar só o "FINISH" exato deixava essas conexões num beco sem saída.
      if (typeof payload.event === 'string' && payload.event.startsWith('FINISH')) {
        signupRef.current.wabaId = payload.data?.waba_id || null
        signupRef.current.phoneNumberId = payload.data?.phone_number_id || null
        setCancelNotice('')

        if (signupRef.current.wabaId) {
          clearNoFinishTimeout()
          if (signupRef.current.code) {
            runExchange()
          }
        } else if (signupRef.current.code) {
          // FINISH chegou, mas sem waba_id (ex.: FINISH_ONLY_WABA sem sessão
          // completa) e o code já veio do FB.login — não espera o timeout:
          // tenta conectar direto, o master descobre a WABA pelo token
          clearNoFinishTimeout()
          runExchange()
        }
      } else if (payload.event === 'CANCEL') {
        clearNoFinishTimeout()
        const step = payload.data?.current_step
        setCancelNotice(
          step
            ? `Conexão com o Facebook cancelada na etapa "${step}".`
            : 'Conexão com o Facebook cancelada.'
        )
      }
    }
  }, [runExchange])

  // registrado uma única vez, antes de qualquer clique no botão
  useEffect(() => {
    const listener = (event) => handleMessageRef.current(event)
    window.addEventListener('message', listener)
    return () => {
      window.removeEventListener('message', listener)
      clearNoFinishTimeout()
    }
  }, [])

  // carrega o SDK assim que o botão entra em tela — não no clique. Assim, no
  // momento do clique o FB.login já roda de forma síncrona (sem esperar rede),
  // que é o que evita o navegador bloquear o popup.
  useEffect(() => {
    if (!isEmbeddedSignupAvailable()) return undefined

    let cancelled = false
    setSdkStatus('loading')

    loadFacebookSdk()
      .then((fb) => {
        if (cancelled) return
        fbRef.current = fb
        setSdkStatus('ready')
      })
      .catch((error) => {
        console.error(error)
        if (!cancelled) setSdkStatus('error')
      })

    return () => { cancelled = true }
  }, [])

  const handleLoginClick = () => {
    if (!fbRef.current) return

    setErrorMessage('')
    setCancelNotice('')
    setFlowStatus('idle')
    clearNoFinishTimeout()
    signupRef.current = { code: null, wabaId: null, phoneNumberId: null }

    // chamada SÍNCRONA, direto no handler do clique — qualquer espera antes
    // (rede, promise) faz o navegador tratar o popup como bloqueado
    fbRef.current.login((response) => {
      if (response?.authResponse?.code) {
        signupRef.current.code = response.authResponse.code
        if (signupRef.current.wabaId) {
          runExchange()
        } else {
          // o code chegou mas o postMessage de FINISH ainda não — dá um tempo
          // (popup pode ainda estar fechando) antes de mostrar erro
          armNoFinishTimeout()
        }
      }
    }, {
      config_id: process.env.REACT_APP_FB_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      extras: {}
    })
  }

  const handlePinSubmit = () => runExchange({ pin })
  const handlePhoneSelected = () => selectedPhoneId && runExchange({ phoneNumberId: selectedPhoneId })

  if (!isEmbeddedSignupAvailable()) return null

  const isBusy = flowStatus === 'connecting'
  const isLoadingSdk = sdkStatus === 'loading'

  return (
    <div>
      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={sdkStatus !== 'ready' || isBusy}
        startIcon={(isBusy || isLoadingSdk) ? <CircularProgress size={18} color="inherit" /> : <FacebookIcon />}
        onClick={handleLoginClick}
        sx={{
          backgroundColor: '#1877F2',
          color: '#fff',
          fontWeight: 700,
          textTransform: 'none',
          '&:hover': { backgroundColor: '#166FE5' }
        }}
      >
        {isBusy ? 'Conectando sua conta do WhatsApp...' : 'Entrar com Facebook'}
      </Button>

      {sdkStatus === 'error' && (
        <Text className="text-xs mt-2" style={{ color: '#ef4444' }}>
          Não foi possível carregar o login do Facebook. Você ainda pode conectar preenchendo os campos manualmente abaixo.
        </Text>
      )}

      {flowStatus === 'error' && errorMessage && (
        <Text className="text-xs mt-2" style={{ color: '#ef4444' }}>
          {errorMessage}
        </Text>
      )}

      <Snackbar
        open={!!cancelNotice}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={() => setCancelNotice('')}
        message={cancelNotice}
      />

      <Dialog open={flowStatus === 'pin-required'} onClose={() => setFlowStatus('idle')}>
        <DialogTitle>PIN de verificação em duas etapas</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Informe o PIN de duas etapas do seu número para concluir a conexão.
          </DialogContentText>
          <div className="mt-4">
            <TextFieldCustom
              label="PIN (6 dígitos)"
              value={pin}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlowStatus('idle')}>Cancelar</Button>
          <Button variant="contained" disabled={pin.length !== 6} onClick={handlePinSubmit}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={flowStatus === 'choose-phone'} onClose={() => setFlowStatus('idle')}>
        <DialogTitle>Selecione o número</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Encontramos mais de um número na sua conta do WhatsApp Business. Selecione qual deseja conectar.
          </DialogContentText>
          <div className="mt-4">
            {/* native: poucos números pra escolher — select nativo é mais
                acessível (teclado/leitor de tela) sem perder nada de usabilidade */}
            <Select
              className="w-full"
              native
              value={selectedPhoneId}
              displayEmpty
              inputProps={{ 'aria-label': 'Número do WhatsApp para conectar' }}
              onChange={e => setSelectedPhoneId(e.target.value)}
            >
              <option value="" disabled>Selecione um número</option>
              {phoneOptions.map(phoneOption => (
                <option key={phoneOption.id} value={phoneOption.id}>
                  {phoneOption.displayPhoneNumber || phoneOption.verifiedName || phoneOption.id}
                </option>
              ))}
            </Select>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlowStatus('idle')}>Cancelar</Button>
          <Button variant="contained" disabled={!selectedPhoneId} onClick={handlePhoneSelected}>
            Conectar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default MetaEmbeddedSignupButton
